
import React, { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlusCircle, Trash2, Instagram, Twitter, Facebook, Linkedin, Github, Youtube, Globe } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface SocialMediaSectionProps {
  socialMedia: Record<string, string>;
  updateBusinessData: (key: string, value: any) => void;
}

// Common social media platforms with their icons
const socialPlatforms = [
  { name: 'Instagram', icon: Instagram, color: "text-pink-500" },
  { name: 'Twitter', icon: Twitter, color: "text-blue-400" },
  { name: 'Facebook', icon: Facebook, color: "text-blue-600" },
  { name: 'LinkedIn', icon: Linkedin, color: "text-blue-700" },
  { name: 'YouTube', icon: Youtube, color: "text-red-600" },
  { name: 'GitHub', icon: Github, color: "text-gray-800 dark:text-gray-200" },
  { name: 'Other', icon: Globe, color: "text-gray-500" }
];

const SocialMediaSection: React.FC<SocialMediaSectionProps> = ({ 
  socialMedia, 
  updateBusinessData 
}) => {
  const { toast } = useToast();
  const [selectedPlatform, setSelectedPlatform] = useState("");
  const [newSocialUrl, setNewSocialUrl] = useState("");
  const [customPlatform, setCustomPlatform] = useState("");

  const updateSocialMedia = (updatedSocialMedia: Record<string, string>) => {
    updateBusinessData('socialMedia', updatedSocialMedia);
  };

  const handleAddSocialMedia = () => {
    const platform = selectedPlatform === 'Other' ? customPlatform : selectedPlatform;
    if (!platform || !newSocialUrl) return;
    
    let url = newSocialUrl;
    // Add https:// if the URL doesn't include it
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }
    
    updateSocialMedia({
      ...socialMedia,
      [platform]: url
    });
    
    toast({
      title: "Social profile added",
      description: `${platform} profile has been added to your business profile.`
    });
    
    setSelectedPlatform("");
    setNewSocialUrl("");
    setCustomPlatform("");
  };

  const handleRemoveSocialMedia = (platform: string) => {
    const updatedSocialMedia = { ...socialMedia };
    delete updatedSocialMedia[platform];
    
    updateSocialMedia(updatedSocialMedia);
    
    toast({
      title: "Social profile removed",
      description: `${platform} profile has been removed from your business profile.`
    });
  };

  const getSocialIcon = (platform: string) => {
    const match = socialPlatforms.find(
      p => p.name.toLowerCase() === platform.toLowerCase()
    );
    if (match) {
      const Icon = match.icon;
      return <Icon className={`h-4 w-4 ${match.color}`} />;
    }
    return <Globe className="h-4 w-4" />;
  };

  const getUrlPreview = (platform: string, url: string): string => {
    // Extract username or handle for different platforms
    if (platform.toLowerCase() === 'instagram') {
      // Extract Instagram handle from URL
      const match = url.match(/instagram\.com\/([^\/\?]+)/);
      return match ? `@${match[1]}` : url;
    }
    if (platform.toLowerCase() === 'twitter') {
      // Extract Twitter handle from URL
      const match = url.match(/twitter\.com\/([^\/\?]+)/);
      return match ? `@${match[1]}` : url;
    }
    // Default: just return the URL
    return url;
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="social-media">Social Media Profiles</Label>
      <Card className="border-dashed">
        <CardContent className="p-4 space-y-4">
          {socialMedia && Object.entries(socialMedia).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(socialMedia).map(([platform, url]) => (
                <div key={platform} className="flex items-center justify-between gap-2 p-2 bg-muted/20 rounded-md">
                  <div className="flex items-center gap-2 overflow-hidden">
                    {getSocialIcon(platform)}
                    <span className="font-medium">{platform}</span>
                    <span className="text-sm text-muted-foreground truncate">
                      {getUrlPreview(platform, url)}
                    </span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => handleRemoveSocialMedia(platform)}
                    className="text-destructive hover:text-destructive/80"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground text-sm p-4">
              No social media profiles added yet.
            </p>
          )}
          
          <div className="space-y-3">
            <div className="flex gap-2">
              <Select value={selectedPlatform} onValueChange={setSelectedPlatform}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Select platform" />
                </SelectTrigger>
                <SelectContent>
                  {socialPlatforms.map(platform => (
                    <SelectItem key={platform.name} value={platform.name}>
                      <div className="flex items-center gap-2">
                        <platform.icon className={`h-4 w-4 ${platform.color}`} />
                        <span>{platform.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {selectedPlatform === 'Other' && (
              <Input
                placeholder="Custom platform name"
                value={customPlatform}
                onChange={(e) => setCustomPlatform(e.target.value)}
              />
            )}
            
            <div className="flex gap-2">
              <Input
                placeholder={
                  selectedPlatform.toLowerCase() === 'instagram' 
                    ? "instagram.com/yourusername"
                    : selectedPlatform.toLowerCase() === 'twitter'
                    ? "twitter.com/yourusername"
                    : "URL (e.g., https://yourplatform.com/profile)"
                }
                value={newSocialUrl}
                onChange={(e) => setNewSocialUrl(e.target.value)}
                className="flex-1"
              />
              <Button 
                variant="outline" 
                onClick={handleAddSocialMedia}
                disabled={(!selectedPlatform || !newSocialUrl) || (selectedPlatform === 'Other' && !customPlatform)}
              >
                <PlusCircle className="h-4 w-4 mr-2" />
                Add
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SocialMediaSection;
